import {
    MutationCache,
    QueryCache,
    QueryClient,
    QueryClientProvider,
} from '@tanstack/react-query';
import { getIdToken } from 'firebase/auth';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, redirect } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { toast } from 'sonner';

import { MUST_CHANGE_PASSWORD_CLAIM } from '@workspace/isomorphic';
import { Toaster } from '@workspace/shadcn';
import { client, createMutationErrorHandler } from '@workspace/ui';

import { auth, getIdentity } from './app/firebase.tsx';
import Layout from './app/layout.tsx';
import ChangePassword from './app/routes/change-password.tsx';
import CourseEditorPage from './app/routes/courses/course-editor.tsx';
import CoursesPage from './app/routes/courses/list-course.tsx';
import InstructorsPage from './app/routes/instructors/list-instructor.tsx';
import SignIn from './app/routes/signin.tsx';
import i18n from './i18n/i18n';

async function readMustChangePassword(): Promise<boolean> {
  const user = auth.currentUser;
  if (!user) return false;
  const tokenResult = await user.getIdTokenResult();
  return Boolean(tokenResult.claims[MUST_CHANGE_PASSWORD_CLAIM]);
}

async function requiresAuth() {
  const identity = await getIdentity();
  if (!identity) {
    throw redirect('/signin');
  }

  if (await readMustChangePassword()) {
    throw redirect('/change-password');
  }

  return identity;
}

async function requiresPasswordChange() {
  const identity = await getIdentity();
  if (!identity) {
    throw redirect('/signin');
  }
  if (!(await readMustChangePassword())) {
    throw redirect('/');
  }
  return identity;
}

const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: createMutationErrorHandler({
      i18n,
      fallbackMessage: 'Something went wrong',
      onUnauthorized: () => {
        toast.error('You are not authorized to perform this action');
        window.location.href = '/';
      },
    }),
  }),
  queryCache: new QueryCache({
    onError: (error) => {
      console.error(error);
    },
  }),
});

const router = createBrowserRouter([
  {
    path: '/signin',
    Component: SignIn,
    loader: async () => {
      const identity = await getIdentity();
      if (!identity) {
        return null;
      }
      throw redirect('/');
    },
  },
  {
    path: '/change-password',
    Component: ChangePassword,
    loader: requiresPasswordChange,
  },
  {
    Component: Layout,
    loader: requiresAuth,
    children: [
      {
        path: '/',
        index: true,
        loader: () => redirect('/courses'),
      },
      {
        path: '/courses',
        Component: CoursesPage,
      },
      // Static segment first: '/courses/new' would otherwise be read as an id.
      {
        path: '/courses/new',
        Component: CourseEditorPage,
      },
      {
        path: '/courses/:id',
        Component: CourseEditorPage,
      },
      {
        path: '/instructors',
        Component: InstructorsPage,
      },
    ],
  },
]);

client.setOptions({
  token: () => {
    if (auth.currentUser) {
      return getIdToken(auth.currentUser);
    }
    return '';
  },
});

createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    <Toaster />
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
